/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useMemo, useReducer } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";
import { ROLE } from "constants";
import { RETURN_REQUEST } from "constants";

// The Material Dashboard 2 PRO React main context
const MaterialUI = createContext();

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = "MaterialUIContext";

// Material Dashboard 2 PRO React reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "WHITE_SIDENAV": {
      return { ...state, whiteSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "DARKMODE": {
      return { ...state, darkMode: action.value };
    }
    case "ORDERCONTEXT": {
      return { ...state, orderContext: action.value };
    }
    case "REVIEWCONTEXT": {
      return { ...state, reviewContext: action.value };
    }
    case "WITHDRAWALCONTEXT": {
      return { ...state, withdrawalContext: action.value };
    }
    case "ROLECONTEXT": {
      return { ...state, roleContext: action.value };
    }
    case "SUBADMINCONTEXT": {
      return { ...state, subadminContext: action.value };
    }
    case "CARRIERCONTEXT": {
      return { ...state, carrierContext: action.value };
    }

    case "CUSTOMERCONTEXT": {
      return { ...state, customerContext: action.value };
    }
    case "RETURNREQUESTCONTEXT": {
      return { ...state, returnRequestContext: action.value }
    }
    case "REFUNDCONTEXT": {
      return { ...state, refundContext: action.value };
    }
    case "TICKETCONTEXT": {
      return { ...state, ticketContext: action.value };
    }
    case "VENDORCONTEXT": {
      return { ...state, vendorContext: action.value };
    }
    case "SERVICECONTEXT": {
      return { ...state, serviceContext: action.value };
    }
    case "PRODUCTCONTEXT": {
      return { ...state, productContext: action.value };
    }
    case "LOGCONTEXT": {
      return { ...state, logContext: action.value };
    }

    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Material Dashboard 2 PRO React context provider
function MaterialUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    whiteSidenav: false,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: false,


    orderContext: {
      orderBy: 'orderid',
      orderByType: 'desc',
      filterBy: 'none',
      searchBy: 'none',
      paymentStatus: 'all',
      orderStatus: 'all',
      timePeriod: 'all',
      searchByValue: '',
    },

    reviewContext: {
      statusFilter: '0',
      ratingFilter: 'all',
      searchBy: 'none',
      searchValue: '',
      orderBy: 'createdAt',
      orderValue: 'DESC',
    },

    withdrawalContext: {
      statusFilter: 'all',
      searchBy: 'none',
      searchValue: '',
      orderBy: 'id',
      orderValue: 'DESC',
    },

    roleContext: {
      statusFilter: ROLE.ALL,
      searchBy: 'none',
      searchValue: ''
    },

    subadminContext: {
      statusFilter: 'all',
      searchBy: 'none',
      searchValue: '',
      orderBy: 'createdAt',
      orderValue: 'DESC',
      timePeriod: 'DESC'
    },

    carrierContext: {
      carrierService: false,
      carrierServiceId: ""
    },

    customerContext: {
      searchBy: 'none',
      searchValue: '',
      orderBy: 'createdAt',
      orderValue: 'DESC'
    },

    returnRequestContext: {
      statusFilter: 'all',
      orderBy: 'id',
      orderValue: 'DESC',
      searchBy: 'none',
      searchValue: ''
    },

    refundContext: {
      orderBy: 'orderid',
      orderValue: 'desc',
      searchBy: 'none',
      searchValue: '',
      statusFilter: 'all',
      typeFilter: 'all'
    },

    ticketContext: {
      status: 'all',
      searchBy: 'none',
      vendorShopName: '',
      customerEmail: ''
    },

    vendorContext: {
      statusFilter: 'all',
      orderBy: 'id',
      orderValue: 'DESC',
      searchBy: 'none',
      searchValue: ''
    },

    serviceContext: {
      statusFilter: 'all',
      orderBy: 'id',
      orderValue: 'DESC',
      searchBy: 'none',
      searchValue: '',
      collectionId: 0
    },

    productContext: {
      statusFilter: 'all',
      orderBy: 'id',
      orderValue: 'DESC',
      searchBy: 'none',
      searchValue: '',
      collectionId: 0
    },

    logContext: {
      source: 'admin',
      logType: 'error'
    }

  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => [controller, dispatch, initialState], [controller, dispatch]);

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

// Material Dashboard 2 PRO React custom hook for using context
function useMaterialUIController() {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }

  return context;
}

// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });

const setOrderContext = (dispatch, value) => dispatch({ type: "ORDERCONTEXT", value });
const setReviewContext = (dispatch, value) => dispatch({ type: "REVIEWCONTEXT", value });
const setWithdrawalContext = (dispatch, value) => dispatch({ type: "WITHDRAWALCONTEXT", value });
const setRoleContext = (dispatch, value) => dispatch({ type: "ROLECONTEXT", value });
const setSubadminContext = (dispatch, value) => dispatch({ type: "SUBADMINCONTEXT", value });
const setCarrierContext = (dispatch, value) => dispatch({ type: "CARRIERCONTEXT", value });
const setCustomerContext = (dispatch, value) => dispatch({ type: "CUSTOMERCONTEXT", value });
const setReturnRequestContext = (dispatch, value) => dispatch({ type: "RETURNREQUESTCONTEXT", value });
const setRefundContext = (dispatch, value) => dispatch({ type: "REFUNDCONTEXT", value });
const setTicketContext = (dispatch, value) => dispatch({ type: "TICKETCONTEXT", value });
const setVendorContext = (dispatch, value) => dispatch({ type: "VENDORCONTEXT", value });
const setServiceContext = (dispatch, value) => dispatch({ type: "SERVICECONTEXT", value });
const setProductContext = (dispatch, value) => dispatch({ type: "PRODUCTCONTEXT", value });
const setLogContext = (dispatch, value) => dispatch({ type: "LOGCONTEXT", value });

export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
  setOrderContext,
  setReviewContext,
  setWithdrawalContext,
  setRoleContext,
  setSubadminContext,
  setCarrierContext,
  setCustomerContext,
  setReturnRequestContext,
  setRefundContext,
  setTicketContext,
  setVendorContext,
  setServiceContext,
  setProductContext,
  setLogContext
};
